// API service with conditional mock/real implementation
import config from '../config';
import { mockApiClient } from './mockApi';
import axios from 'axios';
import { getAuthToken } from './auth';
import { Job, UsageMetrics, UploadRequest, UploadResponse, DownloadUrlResponse } from '../types';

// Real API client implementation
class RealApiClient {
  private client: ReturnType<typeof axios.create>;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiGatewayUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use(
      (config: any) => {
        const token = getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => Promise.reject(error)
    );

    // Handle errors
    this.client.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Upload endpoints
  async requestUpload(request: UploadRequest): Promise<UploadResponse> {
    const response = await this.client.post('/upload/request', request);
    return response.data;
  }

  // Job endpoints
  async getJobs(): Promise<Job[]> {
    const response = await this.client.get('/jobs');
    return response.data.jobs;
  }

  async getJob(jobId: string): Promise<Job> {
    const response = await this.client.get(`/jobs/${jobId}`);
    return response.data;
  }

  async deleteJob(jobId: string): Promise<void> {
    await this.client.delete(`/jobs/${jobId}`);
  }

  async getDownloadUrl(jobId: string, fileType: string): Promise<DownloadUrlResponse> {
    const response = await this.client.get(
      `/jobs/${jobId}/download/${fileType}`
    );
    return response.data;
  }

  // Usage endpoints
  async getUsage(): Promise<UsageMetrics> {
    const response = await this.client.get('/usage');
    return response.data;
  }

  async getAdminUsage(): Promise<UsageMetrics> {
    const response = await this.client.get('/admin/usage');
    return response.data;
  }

  // Admin endpoints
  async getAdminUsers(approvalStatus?: string): Promise<any[]> {
    const params = approvalStatus ? { approvalStatus } : {};
    const response = await this.client.get('/admin/users', { params });
    return response.data.users;
  }

  async updateUserApproval(userId: string, approvalStatus: 'approved' | 'rejected', rejectionReason?: string): Promise<void> {
    if (approvalStatus === 'approved') {
      await this.client.post(`/admin/users/${userId}/approve`);
    } else {
      await this.client.post(`/admin/users/${userId}/reject`, {
        reason: rejectionReason || 'No reason provided',
      });
    }
  }

  // Upload file to S3
  async uploadToS3(url: string, file: File): Promise<void> {
    await axios.put(url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  }
}

// Conditionally export either mock or real API client based on config
let apiClient: any;

if (config.useMockBackend) {
  console.log('[API Service] Using MOCK API client');
  apiClient = mockApiClient;
} else {
  console.log('[API Service] Using AWS API Gateway client');
  apiClient = new RealApiClient();
}

export { apiClient };
